import { createContext, createElement, Fragment, useContext, useMemo, type ReactNode } from "react";
import { istEinzahl, mehrzahlAufloesen } from "@mrmachine/fach";
import de from "./de.json";
import { istPauschale } from "../daten/einmalbetrag";

/* ══════════════════════════════════════════════════════════════════
   Sprache — ein Haken, keine Bibliothek.

   Die Regel aus CLAUDE.md: **Sichtbarer Text gehört nie in den Code.**
   Jede Beschriftung ist ein Schlüssel, von der ersten Maske an. Das
   nachträglich einzuziehen ist der teuerste mechanische Umbau von allen,
   und DingWerk erzwingt es ohnehin per Lint.

   Warum kein react-i18next: Gebraucht wird genau dreierlei — ein
   Schlüssel nachschlagen, Werte einsetzen, ein paar fette Stellen im
   Satz behalten. Das sind vierzig Zeilen. Eine Bibliothek brächte
   Sprachverhandlung und einen Lader mit, von denen hier nichts gebraucht
   wird; sie ließe sich später nachrüsten, ohne dass eine einzige Maske
   sich ändert — der Haken ist dieselbe Form.

   **Einzahl und Mehrzahl** (07.10.2026): Ein Satz mit einer Zahl trägt
   beide Formen selbst — `{zahl, plural, one {# Tag} other {# Tage}}`
   (Schreibweise wie ICU). Die Regel steht in `@mrmachine/fach`
   (`mehrzahlAufloesen`), weil der Server dieselben Sätze liest.
   `apps/web/test/mehrzahl-in-de.test.ts` lehnt einen neuen festen Plural
   wie „{zahl} Tage" ab.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Texteintrag ist entweder ein Satz oder ein weiterer Baum. */
type Baum = { [schluessel: string]: string | string[] | Baum };

/** Was in `{name}` eingesetzt werden darf. */
export type Werte = Record<string, string | number>;

const TEXTE = de as unknown as Baum;

/**
 * Schlägt einen Punktpfad im Baum nach.
 *
 * Fehlt der Schlüssel, kommt er selbst zurück statt einer leeren Stelle:
 * Ein fehlender Text soll in der Maske auffallen, nicht verschwinden.
 */
function nachschlagen(pfad: string): string | string[] {
  /* **Ein fehlender Schlüssel darf keine Maske umbringen** (22.09.2026).
     Gemessen an der laufenden Fassung: Die Jobs-Seite reicht den Pfad aus
     einer Zuordnung je Lauf herein (`JOB_ART_TEXT`); für einen neuen Lauf
     fehlte der Eintrag, `t(undefined)` traf auf `pfad.split` — und statt
     eines fehlenden Wortes stand „Something went wrong!" über der ganzen
     Einstellungsmaske. Die Typen sagen, dass das nicht vorkommt; die Laufzeit
     hat sich nicht daran gehalten. */
  if (typeof pfad !== "string" || pfad === "") return "…";
  let stelle: string | string[] | Baum | undefined = TEXTE;
  for (const teil of pfad.split(".")) {
    if (stelle === undefined || typeof stelle === "string" || Array.isArray(stelle)) return einheitAusDemStamm(pfad) ?? pfad;
    stelle = stelle[teil];
  }
  if (typeof stelle === "string" || Array.isArray(stelle)) return stelle;
  return einheitAusDemStamm(pfad) ?? pfad;
}

/* ══════════════ Einheiten aus dem Stamm ══════════════
   **Eine Zählgröße, die der Betrieb selbst anlegt, hat keinen Satz in dieser
   Datei** (AG-05, 03.10.2026). Die Oberfläche fragt Einheiten über
   `einheit.<code>` ab — für `QM`, `STD`, `MODULE` steht dort ein Wort, für
   eine neue Kennzahl `PB_M` nicht, und in der Liste der Leistungen stand
   `einheit.pb_m`. Die Kataloge bringen zu jeder Zählgröße ihre Einheit mit
   (`metric.unit`); sobald sie geladen sind, trägt `daten/routen.ts` sie hier
   ein. Was dann noch fehlt, zeigt den Code selbst — ein Name aus dem Stamm,
   kein Pfad. */
const EINHEITEN_AUS_DEM_STAMM = new Map<string, string>();

export function einheitenNachtragen(zaehlgroessen: { code: string; einheit?: string; label?: string }[]): void {
  for (const z of zaehlgroessen) {
    const wort = (z.einheit || z.label || z.code || "").trim();
    if (z.code && wort) EINHEITEN_AUS_DEM_STAMM.set(z.code.toLowerCase(), wort);
  }
}

function einheitAusDemStamm(pfad: string): string | null {
  if (!pfad.startsWith("einheit.")) return null;
  const code = pfad.slice("einheit.".length);
  if (!/^[a-z0-9_]+$/i.test(code)) return null;
  return EINHEITEN_AUS_DEM_STAMM.get(code.toLowerCase()) ?? code.toUpperCase();
}

/**
 * Gibt es zu diesem Pfad einen Text? Für die wenigen Stellen, die zwischen
 * einem genauen und einem allgemeinen Satz wählen — etwa ein Fehler von
 * Lexware Office mit einem Schlüssel, den erst Lexware selbst erfunden hat
 * (`daten/klient.ts`, `textPfad`).
 */
export function gibtText(pfad: string): boolean {
  return typeof pfad === "string" && pfad !== "" && nachschlagen(pfad) !== pfad;
}

/** Löst Einzahl/Mehrzahl auf und setzt `{name}` durch den passenden Wert; was fehlt, bleibt stehen. */
export function einsetzen(roh: string, werte?: Werte): string {
  const satz = mehrzahlAufloesen(roh, werte);
  if (!werte) return satz;
  return satz.replace(/\{(\w+)\}/g, (ganz, name: string) =>
    name in werte ? String(werte[name]) : ganz,
  );
}

/**
 * **Die Einheit passend zur Menge** (07.10.2026): „5 Tage × 30,00", aber
 * „1 Tag × 30,00". Ein Einheitenschlüssel `einheit.<code>` wird zu
 * `einheitMehrzahl.<code>`, wenn die Menge nicht genau eins ist und es eine
 * Mehrzahl gibt (m², km, l haben keine). Andere Schlüssel bleiben, wie sie
 * sind. Dieselbe Regel wie `daten/einheit.ts` (P9-06), nur vom Schlüssel aus.
 */
export function einheitFuer(t: (pfad: string) => string, schluessel: string, menge: unknown): string {
  if (typeof schluessel === "string" && schluessel.startsWith("einheit.") && menge !== null && menge !== undefined && !istEinzahl(menge)) {
    const mehrzahl = `einheitMehrzahl.${schluessel.slice("einheit.".length).toLowerCase()}`;
    if (gibtText(mehrzahl)) return t(mehrzahl);
  }
  return t(schluessel);
}

/**
 * **Die Einheit in „Satz × Menge"** (P10-01, Nachprüfung P9): Hinter „je"
 * steht sie in der Einzahl — „98,00 je Stunde × 16", „16 × 98,00 je
 * Stunde" —, nur die Pauschale steht hinter ihrer Menge und folgt ihr
 * („2 Pauschalen × 90,00", `satzMalSchluessel`). Bis dahin bekam „je" an drei
 * Stellen die Mehrzahl der Menge („je Stunden × 16"), und an der Pauschale
 * stand die Einzahl.
 */
export function einheitImSatz(t: (pfad: string) => string, schluessel: string, menge: unknown): string {
  return istPauschale(schluessel) ? einheitFuer(t, schluessel, menge) : t(schluessel);
}

export type Uebersetzer = {
  /** Ein Satz als reine Zeichenkette — für `title`, `aria-label`, `placeholder`. */
  (pfad: string, werte?: Werte): string;
  /** Eine Liste, etwa die Wochentage. */
  liste: (pfad: string) => string[];
};

const Sprachraum = createContext<Uebersetzer | null>(null);

function uebersetzerBauen(): Uebersetzer {
  const t = ((pfad: string, werte?: Werte) => {
    const gefunden = nachschlagen(pfad);
    if (Array.isArray(gefunden)) return gefunden.join(" ");
    return einsetzen(gefunden, werte);
  }) as Uebersetzer;

  t.liste = (pfad: string) => {
    const gefunden = nachschlagen(pfad);
    return Array.isArray(gefunden) ? gefunden : [gefunden];
  };

  return t;
}

export function SprachRaum({ children }: { children: ReactNode }) {
  const wert = useMemo(uebersetzerBauen, []);
  return createElement(Sprachraum.Provider, { value: wert }, children);
}

export function useT(): Uebersetzer {
  const wert = useContext(Sprachraum);
  if (!wert) throw new Error("useT braucht einen SprachRaum darüber.");
  return wert;
}

/* ══════════════ Sätze mit hervorgehobenen Stellen ══════════════
   Viele Sätze in der Oberfläche heben ein Wort hervor — „**Ohne Ist**
   keine Rechnung". Das gehört in den Text, nicht in den Code: Wo die
   Betonung liegt, ist eine Frage der Sprache.

   `<b>…</b>` wird dafür ausgewertet, und zwar durch Zerlegen, nicht
   durch `dangerouslySetInnerHTML`. Der Unterschied zählt: Sobald ein
   Text einmal einen Wert aus der API trägt, wäre das eine offene Tür.
   So bleibt jede eingesetzte Zeichenkette Text. */
const FETT = /<b>([\s\S]*?)<\/b>/g;

function zerlegen(satz: string): ReactNode[] {
  const stuecke: ReactNode[] = [];
  let zuletzt = 0;
  let treffer: RegExpExecArray | null;
  FETT.lastIndex = 0;
  while ((treffer = FETT.exec(satz)) !== null) {
    if (treffer.index > zuletzt) stuecke.push(satz.slice(zuletzt, treffer.index));
    stuecke.push(createElement("b", { key: `b${treffer.index}` }, treffer[1]));
    zuletzt = treffer.index + treffer[0].length;
  }
  if (zuletzt < satz.length) stuecke.push(satz.slice(zuletzt));
  return stuecke;
}

/**
 * Ein Satz mit Auszeichnung: `<T k="regel.drei" />`.
 *
 * Erlaubt ist genau `<b>`. Mehr braucht die Oberfläche nicht, und jede
 * weitere Auszeichnung wäre Gestaltung im Text.
 *
 * Das Ergebnis ist ein Fragment, kein `<span>`: Der Satz sitzt oft direkt
 * in einem Raster oder einer Flex-Zeile, und ein zusätzliches Element
 * darin wäre eine Spalte, die niemand vorgesehen hat.
 */
export function T({ k, werte }: { k: string; werte?: Werte }) {
  const t = useT();
  return createElement(Fragment, null, ...zerlegen(t(k, werte)));
}

/** Dieselbe Zerlegung ohne umschließendes Element — für Stellen, an denen
 *  der Satz direkt in einem `<p>` oder `<b>` sitzt. */
export function useReicheTexte() {
  const t = useT();
  return (pfad: string, werte?: Werte): ReactNode[] => zerlegen(t(pfad, werte));
}
